import { useQuery } from '@tanstack/react-query'
import * as api from '../services/observatoryApi'
import type { ConnectionQuery } from '../types/observatory'
import { FREQUENT, LIVE, SLOW, pollUnlessRefused } from '../utils/polling'

/**
 * The live hooks over services/observatoryApi.ts: the worker fleet, source
 * health, the connection ledger, host configuration and the schedule-load
 * forecast. One hook per endpoint, each with the refresh policy its own comment
 * explains, so a slow or failing panel degrades on its own instead of blanking
 * the page that holds it.
 *
 * The Overview fixture hooks and the old ETL-run hooks that used to live here are
 * deleted, and with them the query keys they shared with the live hooks:
 * ['observatory','liveOperations'], ['observatory','throughput',hours] and
 * ['observatory','etlRun',runId]. ETL runs are read through hooks/useEtlRuns.ts.
 * The intervals come from utils/polling.ts.
 */

/**
 * The worker fleet.
 *
 * On the LIVE interval, not FREQUENT: busy-slot counts change as jobs start and
 * finish, so this is one of the few Overview panels whose underlying numbers
 * genuinely move second to second.
 */
export function useWorkerFleet() {
  return useQuery({ queryKey: ['observatory', 'workers'], queryFn: api.getWorkerFleet, refetchInterval: LIVE })
}

/**
 * Source connection health.
 *
 * On FREQUENT rather than LIVE, which is the opposite call from the worker fleet
 * above and for the same reason: nothing polls faster than it can meaningfully
 * change. `SourceHealthWorker` publishes on a 60-second timer, so a 10-second
 * poll would fetch the identical row three times out of four -- and the figures
 * it carries are contact timestamps and a failure count, which move on the scale
 * of an ETL run rather than a job slot.
 *
 * One key, because this is the only consumer of the endpoint today. Anything
 * that later wants source health on Overview joins THIS query rather than
 * opening a second poll against the same row.
 */
export function useSourceHealth() {
  return useQuery({ queryKey: ['observatory', 'sources'], queryFn: api.getSourceHealth, refetchInterval: FREQUENT })
}

/**
 * The connection ledger, one filtered page at a time.
 *
 * NO refetchInterval, which is the third different answer on this page's three
 * siblings and the only one that is "never". The others poll because they
 * project a present tense that decays -- a worker's heartbeat, a source's
 * reachability. This is an append-only history: rows already fetched cannot
 * change, and an ETL run is something a person starts, so there is no
 * unattended clock for a poll to keep up with. Every ETL job is manual-trigger
 * (cron `0 0 31 2 *` never fires), so on most days a poll would re-fetch an
 * identical page forever.
 *
 * What a poll WOULD cost is not nothing: the query key carries the filters, so
 * each distinct filter combination is its own cache entry with its own timer,
 * and the endpoint runs a COUNT plus an unfiltered MIN on every call. The page
 * offers an explicit refresh instead -- see ConnectionsPage.
 *
 * The filters are part of the key by value. `getConnections` is called through a
 * closure rather than passed by reference for exactly that reason: the key and
 * the argument must move together, or a filter change would serve the previous
 * filter's cached page.
 */
export function useConnections(query: ConnectionQuery) {
  return useQuery({
    queryKey: ['observatory', 'connections', query],
    queryFn: () => api.getConnections(query),
  })
}

/**
 * Host configuration.
 *
 * On SLOW, and it could be slower. No value on this page changes while its
 * process runs -- `IOptionsSnapshot` appears nowhere in `src/` -- so a poll is not
 * watching configuration move. What it IS watching is the page's own standing:
 * Automaton republishes every ten minutes and a host is served as stale after
 * thirty, and a restart moves `processStartedAt`. Two minutes notices both in
 * good time without re-reading an unchanged payload every few seconds. It stops
 * once the server refuses the caller (the endpoint is Lighthouse.Admin): a 403
 * does not change on a second asking.
 */
export function useHostConfiguration() {
  return useQuery({
    queryKey: ['observatory', 'configuration'],
    queryFn: api.getHostConfiguration,
    refetchInterval: pollUnlessRefused(SLOW),
  })
}

/**
 * The schedule-load forecast.
 *
 * On SLOW, for three reasons. The forecast moves on the scale of a quarter-hour:
 * the window opens at the current UTC quarter-hour, so the set of buckets changes
 * every fifteen minutes and not in between. The inputs that move faster — busy
 * slots at the last heartbeat — are context to the forecast, not its subject,
 * and `/workers` already watches them on LIVE. And the payload is the largest
 * any page here fetches: a week over a busy schedule set is thousands of listed
 * occurrences, and the server runs a four-way simulation per request.
 *
 * `zone` and `hours` are both in the key by value, and the function is called
 * through a closure, for the reason `useConnections` gives: the key and the
 * argument must move together, or a new window would serve the previous one's
 * cached forecast under a heading claiming the new one. `enabled` is false with
 * no zone, so a browser that reports none sends nothing rather than a guess.
 */
export function useScheduleLoad(zone: string | null, hours: number) {
  return useQuery({
    queryKey: ['observatory', 'scheduleLoad', zone, hours],
    queryFn: () => api.getScheduleLoad({ zone: zone ?? '', hours }),
    enabled: zone != null,
    refetchInterval: pollUnlessRefused(SLOW),
  })
}
